// Capability + environment detection. Run this BEFORE loading the ~6-8 MB
// MediaPipe bundle so we can show the right fallback instead of downloading
// assets the device can't use.

import type { EngineErrorCode } from './types';

export interface Capabilities {
  supported: boolean;
  /** Set when supported === false. */
  reason?: EngineErrorCode;
  webgl2: boolean;
  wasmSimd: boolean;
  getUserMedia: boolean;
  secureContext: boolean;
  isInAppWebView: boolean;
  tier: 'low' | 'mid' | 'high';
  recommendedDelegate: 'GPU' | 'CPU';
  captureResolution: { width: number; height: number };
}

function detectWebGL2(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return !!canvas.getContext('webgl2');
  } catch {
    return false;
  }
}

/**
 * Minimal WASM SIMD probe — validates a tiny module that uses a v128 op.
 * Bytes are the canonical detector from `wasm-feature-detect`.
 */
function detectWasmSimd(): boolean {
  try {
    return WebAssembly.validate(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10,
        1, 8, 0, 65, 0, 253, 15, 253, 98, 11,
      ]),
    );
  } catch {
    return false;
  }
}

/**
 * In-app webviews (Instagram, Facebook, TikTok, Line) frequently block or
 * silently fail getUserMedia. Detect so we can prompt "open in your browser".
 */
function detectInAppWebView(): boolean {
  const ua = navigator.userAgent || '';
  return /FBAN|FBAV|Instagram|Line\/|TikTok|musical_ly|; wv\)/i.test(ua);
}

function detectTier(): 'low' | 'mid' | 'high' {
  const cores = navigator.hardwareConcurrency || 4;
  // deviceMemory is Chromium-only; treat missing as mid.
  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 4;
  if (cores <= 4 || mem <= 3) return 'low';
  if (cores >= 8 && mem >= 8) return 'high';
  return 'mid';
}

export function detectCapabilities(): Capabilities {
  const secureContext = window.isSecureContext === true;
  const getUserMedia = !!navigator.mediaDevices?.getUserMedia;
  const webgl2 = detectWebGL2();
  const wasmSimd = detectWasmSimd();
  const isInAppWebView = detectInAppWebView();
  const tier = detectTier();

  const recommendedDelegate: 'GPU' | 'CPU' = webgl2 && tier !== 'low' ? 'GPU' : 'CPU';
  const captureResolution =
    tier === 'high'
      ? { width: 1280, height: 720 }
      : tier === 'mid'
        ? { width: 960, height: 540 }
        : { width: 640, height: 480 };

  let supported = true;
  let reason: EngineErrorCode | undefined;
  if (!secureContext) {
    supported = false;
    reason = 'insecure-context';
  } else if (!getUserMedia) {
    supported = false;
    reason = isInAppWebView ? 'webview' : 'unsupported';
  } else if (!webgl2) {
    supported = false;
    reason = 'webgl-failed';
  }

  return {
    supported,
    reason,
    webgl2,
    wasmSimd,
    getUserMedia,
    secureContext,
    isInAppWebView,
    tier,
    recommendedDelegate,
    captureResolution,
  };
}
